import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Badge, PageHead } from '../../components/ui';
import { useToast } from '../../components/Toast';

/**
 * تكامل Google Drive — تخزين الملفات الكبيرة على مستوى النظام.
 *
 * وجهةٌ واحدة يشترك فيها كل شيء ثقيل: تسجيلات المكالمات، ولقطات المراقبة،
 * والمرفقات الضخمة. جمعُها هنا يعني بيانات اعتمادٍ تُدخَل مرة، تُخفّف قاعدة
 * البيانات عن كل ملف يتجاوز بضعة ميغابايت.
 */

interface DriveConfig {
  folderId: string | null;
  clientId: string | null;
  hasClientSecret: boolean;
  hasRefreshToken: boolean;
  ready: boolean;
}

export function DriveIntegration() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const config = useQuery({
    queryKey: ['integrations', 'drive'],
    queryFn: () => api<DriveConfig>('/integrations/drive'),
  });

  const [folderId, setFolderId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [refreshToken, setRefreshToken] = useState('');

  useEffect(() => {
    const c = config.data;
    if (!c) return;
    setFolderId(c.folderId ?? '');
    setClientId(c.clientId ?? '');
  }, [config.data]);

  const save = useMutation({
    mutationFn: () =>
      api('/integrations/drive', {
        method: 'PATCH',
        body: {
          folderId: folderId.trim() || null,
          clientId: clientId.trim() || null,
          clientSecret: clientSecret || undefined,
          refreshToken: refreshToken || undefined,
        },
      }),
    onSuccess: () => {
      toast('حُفظ تكامل Drive');
      setClientSecret('');
      setRefreshToken('');
      void queryClient.invalidateQueries({ queryKey: ['integrations', 'drive'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const test = useMutation({
    mutationFn: () => api<{ link: string }>('/integrations/drive/test', { method: 'POST' }),
    onSuccess: (r) => toast(`نجح الرفع — الملف في Drive: ${r.link.slice(0, 45)}…`),
    onError: (e) => toast(e instanceof Error ? e.message : 'فشل الاختبار'),
  });

  return (
    <div className="page">
      <PageHead
        title="Google Drive"
        subtitle="وجهةُ الملفات الكبيرة — تسجيلات المكالمات ولقطات المراقبة والمرفقات الضخمة"
      />

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">بيانات الاعتماد</h2>
          {config.data && (
            <Badge kind={config.data.ready ? 'success' : 'neutral'}>
              {config.data.ready ? 'جاهز' : 'غير مكتمل'}
            </Badge>
          )}
        </div>
        <div className="card-body">
          <p className="tiny mute mb-3">
            من Google Cloud Console وOAuth Playground بنطاق <code dir="ltr">drive.file</code>. الملفات
            التي تتجاوز أربعة ميغابايت تُرفع هنا تلقائيًّا فتُخفّف قاعدة البيانات.
          </p>

          <div className="form-grid">
            <label className="field">
              <span className="label">معرّف مجلد Drive</span>
              <input
                className="input num"
                dir="ltr"
                value={folderId}
                onChange={(e) => setFolderId(e.target.value)}
                placeholder="1AbC…"
              />
              <span className="tiny mute">المجلد الذي تُرفع إليه الملفات.</span>
            </label>
            <label className="field">
              <span className="label">Client ID</span>
              <input
                className="input num"
                dir="ltr"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
              />
            </label>
            <label className="field">
              <span className="label">
                Client Secret{config.data?.hasClientSecret ? ' — محفوظ' : ''}
              </span>
              <input
                className="input num"
                dir="ltr"
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                autoComplete="new-password"
              />
              <span className="tiny mute">يُحفظ مشفَّرًا ولا يُعرض ثانية.</span>
            </label>
            <label className="field">
              <span className="label">
                Refresh Token{config.data?.hasRefreshToken ? ' — محفوظ' : ''}
              </span>
              <input
                className="input num"
                dir="ltr"
                type="password"
                value={refreshToken}
                onChange={(e) => setRefreshToken(e.target.value)}
                autoComplete="new-password"
              />
            </label>
          </div>

          <div className="row mt-3" style={{ gap: 'var(--sp-2)' }}>
            <button
              className="btn btn-primary"
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              حفظ
            </button>
            {config.data?.ready && (
              <button className="btn" disabled={test.isPending} onClick={() => test.mutate()}>
                {test.isPending ? 'جارٍ الاختبار' : 'اختبر الرفع'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
